import React from 'react';
import PropTypes from 'prop-types';
import { FlightTicketPropType } from '../../../../PropTypes';
import { Card } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useDarkMode, useApiData } from '../../../../Hooks';
import { SettingsApiService, FlightTicketApiService } from '../../../../Services/ApiServices';
import { getReactSelectStyles } from '../../../../Helpers/UiHelper';
import useBusinessSupportForm from './hooks/useBusinessSupportForm';

import PersonalInfoSection from './PersonalInfoSection';
import StaffInfoSection from './StaffInfoSection';
import FlightInfoSection from './FlightInfoSection';
import AdditionalOptionsSection from './AdditionalOptionsSection';
import BookReturnFlightSection from './BookReturnFlightSection';
import RailAndFlySection from './RailAndFlySection';

const BusinessSupportCard = (props) => {
  const { flyToWorkTicketInfo, setRefreshComponent, setFlyToWorkTicketInfo } = props;
  const user = useSelector((state) => state.currentUser);
  const userRoles = user?.roles;
  const { darkMode } = useDarkMode();

  const {
    isPreferredFlightDateValid,
    isPreferredFlightDateDisabled,
    isArrivalDepartureAirportDisabled,
    isOSFieldDisabled,
    isOSFieldDisabledNoTravelNeeded,
    isGreenLightCheckBoxDisabled,
  } = useBusinessSupportForm(userRoles, flyToWorkTicketInfo);

  // Map airports and luggage from settings
  const { data: settings } = useApiData(() => SettingsApiService.getAll());

  // Handle null/undefined flyToWorkTicketInfo gracefully
  if (!flyToWorkTicketInfo) {
    return (
      <Card className={`mb-3 ${darkMode ? 'bg-dark text-light' : ''}`}>
        <Card.Header>
          <i className="fa fa-briefcase me-2"></i>Business Support Information
        </Card.Header>
        <Card.Body>
          <div className="text-muted">No ticket information available</div>
        </Card.Body>
      </Card>
    );
  }
  const settingsKeywords = settings?.keywords || [];

  const airports = settingsKeywords
    .filter((keyword) => keyword.dropdownName === 'airports')
    .flatMap((keyword) => keyword.options.map((option) => ({ value: option.value, label: option.label })));

  const mappedAirports = airports.map((airport) => ({
    value: airport.value,
    label: `${airport.value} - ${airport.label}`,
  }));

  const staffLuggage = settingsKeywords
    .filter((keyword) => keyword.dropdownName === 'staff_luggage')
    .flatMap((keyword) => keyword.options.map((option) => ({ value: option.value, label: option.label })));

  const mappedStaffLuggage = staffLuggage.map((luggage) => ({
    value: luggage.value,
    label: luggage.label,
  }));

  const handleUpdateFlightTicketInfo = (fieldName, fieldValue) => {
    const payload = { [fieldName]: fieldValue };
    FlightTicketApiService.update(flyToWorkTicketInfo.id, payload).then(() => {
      setRefreshComponent((prev) => prev + 1);
    });
  };

  return (
    <Card className="staff-profile-card">
      <Card.Header>
        <i className="fa fa-briefcase me-2"></i>Business Support Information
      </Card.Header>
      <Card.Body>
        <PersonalInfoSection flyToWorkTicketInfo={flyToWorkTicketInfo} />
        <hr />
        <StaffInfoSection flyToWorkTicketInfo={flyToWorkTicketInfo} />
        <hr />
        <FlightInfoSection
          flyToWorkTicketInfo={flyToWorkTicketInfo}
          mappedAirports={mappedAirports}
          handleUpdateFlightTicketInfo={handleUpdateFlightTicketInfo}
          isPreferredFlightDateDisabled={isPreferredFlightDateDisabled}
          isPreferredFlightDateValid={isPreferredFlightDateValid}
          isArrivalDepartureAirportDisabled={isArrivalDepartureAirportDisabled}
          isOSFieldDisabled={isOSFieldDisabled}
          darkMode={darkMode}
          getReactSelectStyles={getReactSelectStyles}
        />
        <hr />
        <AdditionalOptionsSection
          flyToWorkTicketInfo={flyToWorkTicketInfo}
          setFlyToWorkTicketInfo={setFlyToWorkTicketInfo}
          mappedAirports={mappedAirports}
          mappedStaffLuggage={mappedStaffLuggage}
          handleUpdateFlightTicketInfo={handleUpdateFlightTicketInfo}
          isOSFieldDisabled={isOSFieldDisabled}
          isOSFieldDisabledNoTravelNeeded={isOSFieldDisabledNoTravelNeeded}
          isArrivalDepartureAirportDisabled={isArrivalDepartureAirportDisabled}
          isGreenLightCheckBoxDisabled={isGreenLightCheckBoxDisabled}
          darkMode={darkMode}
          getReactSelectStyles={getReactSelectStyles}
        />
        <BookReturnFlightSection
          flyToWorkTicketInfo={flyToWorkTicketInfo}
          mappedAirports={mappedAirports}
          handleUpdateFlightTicketInfo={handleUpdateFlightTicketInfo}
          isArrivalDepartureAirportDisabled={isArrivalDepartureAirportDisabled}
          darkMode={darkMode}
          getReactSelectStyles={getReactSelectStyles}
        />
        <RailAndFlySection
          flyToWorkTicketInfo={flyToWorkTicketInfo}
          setFlyToWorkTicketInfo={setFlyToWorkTicketInfo}
          handleUpdateFlightTicketInfo={handleUpdateFlightTicketInfo}
        />
      </Card.Body>
    </Card>
  );
};

BusinessSupportCard.propTypes = {
  flyToWorkTicketInfo: FlightTicketPropType.isRequired,
  setRefreshComponent: PropTypes.func.isRequired,
  setFlyToWorkTicketInfo: PropTypes.func,
  refreshComponent: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default BusinessSupportCard;
